// src/components/FeaturesShowcaseDark.tsx
import { motion } from "framer-motion";
import { Building, Wrench, Briefcase, Users } from "lucide-react";

const cardBase =
  "group relative overflow-hidden rounded-2xl bg-canvas-300 p-6 md:p-7 border border-beige-300 " +
  "shadow-[0_2px_6px_rgba(0,0,0,0.10),0_16px_40px_rgba(0,0,0,0.08)] " +
  "transition-[box-shadow] duration-300";

const hoverShine =
  "hover:shadow-[0_4px_10px_rgba(0,0,0,0.12),0_22px_60px_rgba(0,0,0,0.12)] " +
  "before:pointer-events-none before:absolute before:inset-0 " +
  "before:bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0)_35%,rgba(255,255,255,0.3)_50%,rgba(255,255,255,0)_65%,transparent_100%)] " +
  "before:opacity-0 hover:before:opacity-100 before:translate-x-[-60%] hover:before:translate-x-[60%] " +
  "before:transition-all before:duration-800 before:ease-out";

const iconWrap =
  "h-10 w-10 rounded-xl bg-wood-800 text-canvas-50 flex items-center justify-center flex-shrink-0 " +
  "shadow-[0_6px_18px_rgba(107,76,50,0.35)] group-hover:shadow-[0_6px_22px_rgba(107,76,50,0.45)] transition-shadow";


// Dark surface cards: cocoa + charcoal with warm wood highlights
const cardBaseDark =
  "group relative overflow-hidden rounded-2xl bg-charcoal-900 p-6 md:p-7 border border-charcoal-800 " +
  "shadow-[0_6px_18px_rgba(107,76,50,0.35)] ";


const hoverShineDark =
  "before:pointer-events-none before:absolute before:inset-0 " +
  "before:bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0)_35%,rgba(255,255,255,0.12)_50%,rgba(255,255,255,0)_65%,transparent_100%)] " +
  "before:opacity-0 hover:before:opacity-100 before:translate-x-[-60%] hover:before:translate-x-[60%] " +
  "before:transition-all before:duration-800 before:ease-out";

// Icon chips: deep wood with gold tint on hover
const iconWrapDark =
  "h-10 w-10 rounded-xl bg-canvas-300 text-wood-800 flex items-center justify-center flex-shrink-0 " +
  "shadow-[0_6px_18px_rgba(92,64,44,0.50)] group-hover:shadow-[0_6px_22px_rgba(92,64,44,0.65)] transition-shadow";

export default function FeaturesShowcaseDark() {
  return (
        <><section className="w-full bg-charcoal-900  ">
          <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
                  <div className="flex flex-col gap-6 md:gap-8">
                      <motion.div
                          initial={{ opacity: 0, x: -64 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                          viewport={{ once: true, amount: 0.6 }}
                          className={`${cardBase} ${hoverShine}`}
                          whileHover={{
                              boxShadow: "0 4px 10px rgba(0,0,0,0.12), 0 22px 60px rgba(0,0,0,0.12)"
                          }}
                      >
                          <div className="flex items-start gap-4">
                              <div className={iconWrap}><Building className="h-5 w-5" /></div>
                              <div>
                                  <h3 className="text-charcoal-900 font-semibold text-base">Versatile Construction Leader</h3>
                                  <p className="text-charcoal-600 text-sm mt-1.5">
                                      A self-made professional, Mr. Nasir Patel excels in project execution, contract management, and on-site coordination. With experience across residential, commercial, and institutional projects, he brings strong technical and managerial expertise to every development.
                                  </p>
                              </div>
                          </div>
                      </motion.div>

                      <motion.div
                          initial={{ opacity: 0, x: -64 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.30, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                          viewport={{ once: true, amount: 0.6 }}
                          className={`${cardBase} ${hoverShine}`}
                          whileHover={{
                              boxShadow: "0 4px 10px rgba(0,0,0,0.12), 0 22px 60px rgba(0,0,0,0.12)"
                          }}
                      >
                          <div className="flex items-start gap-4">
                              <div className={iconWrap}><Wrench className="h-5 w-5" /></div>
                              <div>
                                  <h3 className="text-charcoal-900 font-semibold text-base">Engineering Execution Expert</h3>
                                  <p className="text-charcoal-600 text-sm mt-1.5">
                                      With an ability to bridge the technical rigor of engineering with the practical realities of site execution, Mr. Patel plays a pivotal role in translating design intent into built form.
                                  </p>
                              </div>
                          </div>
                      </motion.div>
                  </div>

                  <motion.div
                      initial={{ opacity: 0, y: 36, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0, duration: 0.1, ease: [0.22, 1, 0.36, 1] }}
                      viewport={{ once: true, amount: 0.6 }}
                      className="order-first md:order-none"
                  >
                      <div
                          className={"group relative mx-auto w-full max-w-md rounded-2xl overflow-hidden " +
                              "shadow-[0_3px_10px_rgba(0,0,0,0.12),0_28px_70px_rgba(0,0,0,0.12)] " +
                              "transition-[box-shadow] duration-300 " +
                              "before:absolute before:inset-0 before:bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0)_40%,rgba(255,255,255,0.28)_50%,rgba(255,255,255,0)_60%,transparent_100%)] before:opacity-0 " +
                              "hover:before:opacity-100 before:translate-x-[-60%] hover:before:translate-x-[60%] before:transition-all before:duration-900 before:ease-out"}
                      >
                          <img src="/images/Nazir.jpg" alt="Professional workspace" className="w-full h-[450px] object-cover rounded-2xl" />
                          <div className="absolute bottom-0 left-0 right-0 rounded-b-2xl bg-[color:rgba(43,34,29,0.45)] backdrop-blur-lg border-t border-[rgba(255,255,255,0.15)] p-[0.7rem]">
                              <h2 className="text-canvas-50 text-2xl font-bold tracking-tight">MR. NASIR PATEL</h2>
                              <p className="text-gold-400 text-sm font-semibold mt-0.5 tracking-wide">FOUNDER & TECHNICAL DIRECTOR</p>
                          </div>
                      </div>
                  </motion.div>

                  <div className="flex flex-col gap-6 md:gap-8">
                      <motion.div
                          initial={{ opacity: 0, x: 64 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.45, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                          viewport={{ once: true, amount: 0.6 }}
                          className={`${cardBase} ${hoverShine}`}
                          whileHover={{
                              boxShadow: "0 4px 10px rgba(0,0,0,0.12), 0 22px 60px rgba(0,0,0,0.12)"
                          }}
                      >
                          <div className="flex items-start gap-4">
                              <div className={iconWrap}><Briefcase className="h-5 w-5" /></div>
                              <div>
                                  <h3 className="text-charcoal-900 font-semibold text-base">Strategic Operations Leader</h3>
                                  <p className="text-charcoal-600 text-sm mt-1.5">
                                      Guided by discipline, foresight, and a commitment to excellence, he has earned a reputation for grounded decision-making, effective resource management, and hands-on problem-solving.
                                  </p>
                              </div>
                          </div>
                      </motion.div>

                      <motion.div
                          initial={{ opacity: 0, x: 64 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.60, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                          viewport={{ once: true, amount: 0.6 }}
                          className={`${cardBase} ${hoverShine}`}
                          whileHover={{
                              boxShadow: "0 4px 10px rgba(0,0,0,0.12), 0 22px 60px rgba(0,0,0,0.12)"
                          }}
                      >
                          <div className="flex items-start gap-4">
                              <div className={iconWrap}><Users className="h-5 w-5" /></div>
                              <div>
                                  <h3 className="text-charcoal-900 font-semibold text-base">Pragmatic Project Leader</h3>
                                  <p className="text-charcoal-600 text-sm mt-1.5">
                                      His leadership ensures design integrity is maintained on ground—on time, within budget, and to specification with an ability to bridge the technical with the practical anchors every Manar project in real-world success.
                                  </p>
                              </div>
                          </div>
                      </motion.div>
                  </div>
              </div>
          </div>
      </section>
      <section className="w-full bg-canvas-300">
              <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">

                      {/* Left column */}
                      <div className="flex flex-col gap-6 md:gap-8">
                          <motion.div
                              initial={{ opacity: 0, x: -64 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                              viewport={{ once: true, amount: 0.6 }}
                              className={`${cardBaseDark} ${hoverShineDark}`}

                          >
                              <div className="flex items-start gap-4">
                                  <div className={iconWrapDark}><Building className="h-5 w-5" /></div>
                                  <div>
                                      <h3 className="text-canvas-50 font-semibold text-base">Human-Centered Designer</h3>
                                      <p className="text-charcoal-400 text-sm mt-1.5">
                                            Ar. Arzoo Patel  brings a human-centered, execution-ready approach to design Her approach reflects a strong belief that great design lies at the intersection of creativity, functionality, and precision.
                                      </p>
                                  </div>
                              </div>
                          </motion.div>

                          <motion.div
                              initial={{ opacity: 0, x: -64 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.30, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                              viewport={{ once: true, amount: 0.6 }}
                              className={`${cardBaseDark} ${hoverShineDark}`}
                              whileHover={{
                                  boxShadow: "0 4px 14px rgba(0,0,0,0.45), 0 26px 70px rgba(0,0,0,0.50)"
                              }}
                          >
                              <div className="flex items-start gap-4">
                                  <div className={iconWrapDark}><Wrench className="h-5 w-5" /></div>
                                  <div>
                                      <h3 className="text-canvas-50 font-semibold text-base">Collaborative Design Expert</h3>
                                      <p className="text-charcoal-400 text-sm mt-1.5">
                                        Driven by a meticulous eye for detail and a collaborative spirit, she is known for her ability to translate ideas into buildable, efficient realities, fostering communication across architecture, services, and construction teams. 
                                      </p>
                                  </div>
                              </div>
                          </motion.div>
                      </div>

                      {/* Center image with dark glass */}
                      <motion.div
                          initial={{ opacity: 0, y: 36, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: 0, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                          viewport={{ once: true, amount: 0.6 }}
                          className="order-first md:order-none"
                      >
                          <div
                              className={"group relative mx-auto w-full max-w-md rounded-2xl overflow-hidden " +
                                  "shadow-[0_6px_18px_rgba(107,76,50,0.35)] group-hover:shadow-[0_6px_22px_rgba(107,76,50,0.45)] transition-shadow " +
                                  "before:absolute before:inset-0 before:bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0)_40%,rgba(255,255,255,0.12)_50%,rgba(255,255,255,0)_60%,transparent_100%)] before:opacity-0 " +
                                  "hover:before:opacity-100 before:translate-x-[-60%] hover:before:translate-x-[60%] before:transition-all before:duration-900 before:ease-out"}
                          >
                              <img src="/images/Arzoo3.jpg" alt="Professional workspace" className="w-full h-[450px] object-cover rounded-2xl" />
                              <div className="absolute bottom-0 left-0 right-0 rounded-b-2xl bg-[color:rgba(27,22,19,0.55)] backdrop-blur-lg border-t border-[rgba(255,255,255,0.08)] p-[0.7rem]">
                                  <h2 className="text-canvas-50 text-2xl font-bold tracking-tight">AR. ARZOO PATEL</h2>
                                  <p className="text-gold-400 text-sm font-semibold mt-0.5 tracking-wide">DESIGN DIRECTOR</p>
                              </div>
                          </div>
                      </motion.div>

                      {/* Right column */}
                      <div className="flex flex-col gap-6 md:gap-8">
                          <motion.div
                              initial={{ opacity: 0, x: 64 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.45, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                              viewport={{ once: true, amount: 0.6 }}
                              className={`${cardBaseDark} ${hoverShineDark}`}
                              whileHover={{
                                  boxShadow: "0 4px 14px rgba(0,0,0,0.45), 0 26px 70px rgba(0,0,0,0.50)"
                              }}
                          >
                              <div className="flex items-start gap-4">
                                  <div className={iconWrapDark}><Briefcase className="h-5 w-5" /></div>
                                  <div>
                                      <h3 className="text-canvas-50 font-semibold text-base">Architectural Project Specialist</h3>
                                      <p className="text-charcoal-400 text-sm mt-1.5">
Qualification: With a foundation in architecture with masters in project management over 5 years of hands-on experience                                      </p>
                                  </div>
                              </div>
                          </motion.div>

                          <motion.div
                              initial={{ opacity: 0, x: 64 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.60, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                              viewport={{ once: true, amount: 0.6 }}
                              className={`${cardBaseDark} ${hoverShineDark}`}
                              whileHover={{
                                  boxShadow: "0 4px 14px rgba(0,0,0,0.45), 0 26px 70px rgba(0,0,0,0.50)"
                              }}
                          >
                              <div className="flex items-start gap-4">
                                  <div className={iconWrapDark}><Users className="h-5 w-5" /></div>
                                  <div>
                                      <h3 className="text-canvas-50 font-semibold text-base">Design Coordination Expert</h3>
                                      <p className="text-charcoal-400 text-sm mt-1.5">
From high-end residences and commercial interiors to site offices and sample flats, she has  led Design coordination across 18+ multidisciplinary projects with top notch consultants in pune — ensuring every project is both aesthetically refined and practically buildable.
                                      </p>
                                  </div>
                              </div>
                          </motion.div>
                      </div>

                  </div>
              </div>
          </section></>
    
  );
}
